import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { InstituteProps } from '@/types';
import instituteService from '@/api/institute.service';
import { updateInstituteInActiveList, updateInstituteInInactiveList } from '@/redux/states';
import { tokens } from '@/utilities';

export interface SubscriptionTierInfo {
  level: number;
  title: string;
  description: string;
}

export const SUBSCRIPTION_LEVELS: SubscriptionTierInfo[] = [
  {
    level: 0,
    title: 'Nivel 0 - Fremium',
    description: 'Perfil institucional básico en la plataforma, sin acceso a analítica comparativa ni funciones avanzadas.',
  },
  {
    level: 1,
    title: 'Nivel 1 - Básico',
    description: 'Acceso a analítica comparativa básica de programas e institución.',
  },
  {
    level: 2,
    title: 'Nivel 2 - Estándar',
    description: 'Gestión ampliada de facultades, programas académicos y analítica básica.',
  },
  {
    level: 3,
    title: 'Nivel 3 - Premium',
    description: 'Acceso completo a analítica de redirecciones de carreras y métricas clave.',
  },
  {
    level: 4,
    title: 'Nivel 4 - Profesional',
    description: 'Acceso extendido a analítica de demanda estudiantil y exportación de reportes.',
  },
  {
    level: 5,
    title: 'Nivel 5 - Corporativo',
    description: 'Posicionamiento destacado en búsquedas y soporte institucional prioritario.',
  },
  {
    level: 6,
    title: 'Nivel 6 - Enterprise',
    description: 'Visibilidad institucional ampliada y tableros en tiempo real.',
  },
  {
    level: 7,
    title: 'Nivel 7 - Elite',
    description: 'Métricas predictivas, campañas institucionales y reportes personalizados.',
  },
  {
    level: 8,
    title: 'Nivel 8 - Platinum',
    description: 'Acceso total a todos los módulos analíticos y descargas avanzadas ilimitadas.',
  },
  {
    level: 9,
    title: 'Nivel 9 - Diamond',
    description: 'Máxima visibilidad de marca y asesoría personalizada de posicionamiento.',
  },
  {
    level: 10,
    title: 'Nivel 10 - Máximo / Ilimitado',
    description: 'Licencia institucional completa con acceso irrestricto a todas las funcionalidades presentes y futuras.',
  },
];

export interface ModalUpdateSubscriptionLevelProps {
  open: boolean;
  onClose: () => void;
  institute: InstituteProps | null;
  onUpdated?: (updatedInstitute: InstituteProps) => void;
}

export const ModalUpdateSubscriptionLevel: React.FC<ModalUpdateSubscriptionLevelProps> = ({
  open,
  onClose,
  institute,
  onUpdated,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  const currentLevel = institute?.subscriptionLevel ?? 0;
  const [selectedLevel, setSelectedLevel] = useState<number>(currentLevel);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (institute) {
      setSelectedLevel(institute.subscriptionLevel ?? 0);
      setErrorMessage(null);
    }
  }, [institute, open]);

  if (!institute) return null;

  const currentTierInfo =
    SUBSCRIPTION_LEVELS.find((tier) => tier.level === currentLevel) ?? SUBSCRIPTION_LEVELS[0];
  const selectedTierInfo =
    SUBSCRIPTION_LEVELS.find((tier) => tier.level === selectedLevel) ?? SUBSCRIPTION_LEVELS[0];

  const hasChanged = selectedLevel !== currentLevel;

  const handleSave = async () => {
    if (!institute) return;

    if (!hasChanged) {
      onClose();
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const targetIdentifier = institute.id || institute.code;
      const success = await instituteService.updateSubscriptionLevel(targetIdentifier, selectedLevel);

      if (success) {
        const updatedInstitute: InstituteProps = {
          ...institute,
          subscriptionLevel: selectedLevel,
        };

        // Actualizar en el store de Redux para que la tabla y vistas se reflejen de inmediato
        dispatch(updateInstituteInActiveList(updatedInstitute));
        dispatch(updateInstituteInInactiveList(updatedInstitute));

        if (onUpdated) {
          onUpdated(updatedInstitute);
        }

        Swal.fire({
          title: 'Nivel de suscripción actualizado',
          html: `La institución <strong>${institute.name}</strong> ahora cuenta con <strong>Nivel ${selectedLevel}</strong> (${selectedTierInfo.title}).`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
        });

        onClose();
      } else {
        setErrorMessage(
          'No se pudo actualizar el nivel de suscripción en la base de datos. Por favor verifica tu conexión e inténtalo nuevamente.'
        );
      }
    } catch (error) {
      console.error('Error al actualizar nivel de suscripción:', error);
      setErrorMessage(
        'Ocurrió un error inesperado al intentar guardar los cambios. Intenta nuevamente.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const marks = [
    { value: 0, label: '0' },
    { value: 2, label: '2' },
    { value: 4, label: '4' },
    { value: 6, label: '6' },
    { value: 8, label: '8' },
    { value: 10, label: '10' },
  ];

  return (
    <Dialog
      open={open}
      onClose={!isLoading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          backgroundColor: theme.palette.mode === 'dark' ? colors.primary[500] : '#FFFFFF',
        },
      }}
      aria-labelledby="update-subscription-title"
    >
      <DialogTitle
        id="update-subscription-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          pt: 2.5,
          px: 3,
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: '#3F51B5',
              width: 42,
              height: 42,
              boxShadow: '0 2px 8px rgba(63, 81, 181, 0.35)',
            }}
          >
            <WorkspacePremiumIcon sx={{ color: '#FFD700', fontSize: 26 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" component="h2" fontWeight={700} color="text.primary">
              Actualizar Nivel de Suscripción
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Super Administrador • Gestión de niveles (0 al 10)
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="Cerrar modal de actualización de suscripción"
          onClick={onClose}
          disabled={isLoading}
          sx={{
            minWidth: 44,
            minHeight: 44,
            color: colors.grey[300],
            '&:hover': { color: colors.grey[100] },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        {/* Tarjeta de información de la institución */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F4F7FC',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {institute.logo ? (
            <Avatar
              src={institute.logo}
              alt={`Logo de ${institute.name}`}
              sx={{ width: 56, height: 56, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}
              variant="rounded"
            />
          ) : (
            <Avatar
              sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: '#007FC0' }}
              variant="rounded"
            >
              <BusinessIcon fontSize="large" />
            </Avatar>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              noWrap
              title={institute.name}
              sx={{ color: 'text.primary' }}
            >
              {institute.name}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
              {institute.code && (
                <Chip
                  size="small"
                  label={`Cód: ${institute.code}`}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.72rem' }}
                />
              )}
              {institute.character && (
                <Chip
                  size="small"
                  label={institute.character}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.72rem' }}
                />
              )}
              <Chip
                size="small"
                label={`Nivel actual: ${currentLevel}`}
                color={currentLevel > 0 ? 'primary' : 'default'}
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Selector interactivo de nivel */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
            Selecciona el nuevo nivel de suscripción (0 al 10):
          </Typography>

          <FormControl fullWidth size="medium" sx={{ mb: 3 }}>
            <InputLabel id="select-subscription-level-label">Nivel de Suscripción</InputLabel>
            <Select
              labelId="select-subscription-level-label"
              id="select-subscription-level"
              value={selectedLevel}
              label="Nivel de Suscripción"
              onChange={(e) => setSelectedLevel(Number(e.target.value))}
              aria-label="Seleccionar nuevo nivel de suscripción del 0 al 10"
              disabled={isLoading}
              sx={{
                borderRadius: 2,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 600,
                },
              }}
            >
              {SUBSCRIPTION_LEVELS.map((tier) => (
                <MenuItem
                  key={tier.level}
                  value={tier.level}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WorkspacePremiumIcon
                      sx={{
                        fontSize: 20,
                        color: tier.level > 0 ? '#3F51B5' : colors.grey[400],
                      }}
                    />
                    <Typography variant="body2" fontWeight={tier.level === selectedLevel ? 700 : 500}>
                      {tier.title}
                    </Typography>
                  </Box>
                  {tier.level === currentLevel && (
                    <Chip
                      label="Actual"
                      size="small"
                      sx={{ height: 20, fontSize: '0.68rem', ml: 2 }}
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Slider visual */}
          <Box sx={{ px: 2, mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Ajuste rápido mediante barra deslizadora:
            </Typography>
            <Slider
              value={selectedLevel}
              min={0}
              max={10}
              step={1}
              marks={marks}
              valueLabelDisplay="auto"
              onChange={(_, value) => setSelectedLevel(value as number)}
              aria-label="Selector deslizable de nivel de suscripción del 0 al 10"
              disabled={isLoading}
              sx={{
                color: '#3F51B5',
                height: 8,
                '& .MuiSlider-track': {
                  border: 'none',
                },
                '& .MuiSlider-thumb': {
                  height: 22,
                  width: 22,
                  backgroundColor: '#fff',
                  border: '3px solid #3F51B5',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(63, 81, 181, 0.16)',
                  },
                },
                '& .MuiSlider-valueLabel': {
                  lineHeight: 1.2,
                  fontSize: 12,
                  background: 'unset',
                  padding: 0,
                  width: 32,
                  height: 32,
                  borderRadius: '50% 50% 50% 0',
                  backgroundColor: '#3F51B5',
                  transformOrigin: 'bottom left',
                  transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
                  '&:before': { display: 'none' },
                  '&.MuiSlider-valueLabelOpen': {
                    transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
                  },
                  '& > *': {
                    transform: 'rotate(45deg)',
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Tarjeta de descripción y comparación del nivel seleccionado */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? selectedLevel > 0
                  ? 'rgba(63, 81, 181, 0.15)'
                  : 'rgba(255,255,255,0.03)'
                : selectedLevel > 0
                ? '#EEF2FF'
                : '#F8FAFC',
            border: `1px solid ${
              selectedLevel > 0 ? 'rgba(63, 81, 181, 0.3)' : 'rgba(0,0,0,0.08)'
            }`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkspacePremiumIcon
                sx={{
                  color: selectedLevel > 0 ? '#3F51B5' : colors.grey[400],
                  fontSize: 22,
                }}
              />
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                {selectedTierInfo.title}
              </Typography>
            </Box>

            {/* Indicador de cambio */}
            {hasChanged ? (
              selectedLevel > currentLevel ? (
                <Chip
                  icon={<TrendingUpIcon />}
                  label={`Subir: Nivel ${currentLevel} → ${selectedLevel}`}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600, height: 24 }}
                />
              ) : (
                <Chip
                  icon={<TrendingDownIcon />}
                  label={`Bajar: Nivel ${currentLevel} → ${selectedLevel}`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 600, height: 24 }}
                />
              )
            ) : (
              <Chip
                icon={<RemoveIcon />}
                label="Sin cambios"
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {selectedTierInfo.description}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
          sx={{
            minHeight: 44,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            borderColor: colors.grey[400],
            color: colors.grey[100],
          }}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || !hasChanged}
          aria-label={`Guardar y actualizar nivel de suscripción a ${selectedLevel}`}
          sx={{
            minHeight: 44,
            px: 3.5,
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            backgroundColor: '#3F51B5',
            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.35)',
            '&:hover': {
              backgroundColor: '#002D82',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
              <span>Guardando...</span>
            </Box>
          ) : (
            `Guardar Nivel ${selectedLevel}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalUpdateSubscriptionLevel;
